import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Activity,
  AtSign,
  Award,
  CalendarDays,
  ClipboardCheck,
  Fish,
  GraduationCap,
  History,
  LayoutGrid,
  ListChecks,
  Mail,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Trophy,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import IdentityBand from '../../components/IdentityBand'
import PageHeader from '../../components/PageHeader'
import useGoBack from '../../components/useGoBack'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import StatusBadge from '../../components/StatusBadge'
import SectionNav from '../../components/SectionNav'
import PageLoader from '../../components/PageLoader'
import { ErrorState } from '../../components/States'
import { FEATURES } from '../../app/navigation'
import { useSession } from '../../lib/sessionContext'
import PointsTab from './PointsTab'
import ProgramsTab from './ProgramsTab'
import {
  ActivitiesTab,
  AssessmentsTab,
  ExamTriesTab,
  PhishingTab,
  PointsLogTab,
  PoliciesTab,
  SecurityReportsTab,
  StatusLogTab,
  SurveysTab,
} from './tabs'
import {
  getUser,
  listActivities,
  listAssessments,
  listExamTries,
  listPhishing,
  listPointsLog,
  listPolicies,
  listSecurityReports,
  listStatusLog,
  listSurveys,
  resetExamTry,
  downloadSurveyReport,
} from './userDetailsService'
import { strings as S } from './strings'
import './userDetails.css'

/**
 * The license feature flags that gate the last six tabs. The legacy reads these from
 * settings (`Ctrl:634-638`); an empty license grants everything, exactly as the side
 * menu does.
 */
const featureIsOn = (features, feature) => features.length === 0 || features.includes(feature)

const asCell = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '')

/**
 * التقارير الشخصية — Views/UserDetailsAdmin/Index.cshtml.
 *
 * The identity header shows the three values the legacy renders; its avatar block is
 * `display:none !important` there, so the initials avatar here is the redesigned
 * stand-in for the username line rather than an added element.
 */
export default function UserDetailsPage() {
  /* This screen is always opened FROM somewhere — the users list, a log row, a
     group's members — so Back is the way out, and the list it returns to is the
     one it was left on. */
  const goBack = useGoBack('/users')
  const { session } = useSession()
  const featureOn = (feature) => featureIsOn(session.features, feature)
  const { id } = useParams()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  /** Bumped after a reset so the remaining-tries column re-reads. */
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getUser(id)
      .then((response) => {
        if (cancelled) return
        setUser(response)
        setLoading(false)
      })
      .catch(() => {
        /* A refused role or an unknown username lands on the not-found state below,
           rather than a spinner that never stops. */
        if (cancelled) return
        setUser(null)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  /* Both of these used to toast success without calling anything — the exam try was
     never reset and no report was ever downloaded. The row is passed in, so each has
     the id its endpoint wants. */
  const onResetTries = useCallback(
    async (row) => {
      const outcome = await resetExamTry(user.userId, row.examId)
      if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
      toast.success(S.examTries.resetDone)
      /* The remaining-tries column is what the reset changes, so the table is reloaded
         rather than left showing the old count. */
      setReloadKey((key) => key + 1)
    },
    [toast, user?.userId],
  )

  const onDownloadSurvey = useCallback(
    async (row) => {
      const outcome = await downloadSurveyReport({ userId: user.userId, surveyId: row.surveyId })
      if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
      toast.success(S.surveys.downloaded)
    },
    [toast, user?.userId],
  )

  const totalBadges = user?.badges?.length ?? 0
  const earnedBadges = user?.badges?.filter((badge) => badge.earned).length ?? 0

  /**
   * Eleven sections, in three subjects.
   *
   * The legacy has them as one flat row of tabs and the redesign kept that — it
   * worked while six of the eleven were missing. All eleven need well past the
   * page's width, and a strip that scrolls hides the ones that did not fit
   * behind a drag nothing announces. SectionNav wraps instead, and wraps on
   * GROUP boundaries, which is what makes three subjects out of eleven words:
   * what the user did, what they were asked to learn, how they stood up to
   * being tested on it. Gamification stands alone, because it scores all three.
   *
   * The gating is the legacy's (`Ctrl:634-638`) — an empty licence grants
   * everything, exactly as the side menu reads it.
   */
  const groups = useMemo(
    () =>
      [
        {
          items: [
            { id: 'activities', label: S.tabs.activities, icon: Activity },
            { id: 'statusLogs', label: S.tabs.statusLogs, icon: History },
          ],
        },
        {
          items: [
            featureOn(FEATURES.Lms) && {
              id: 'programs',
              label: S.tabs.programs,
              icon: GraduationCap,
            },
            { id: 'examTries', label: S.tabs.examTries, icon: RotateCcw },
            featureOn(FEATURES.Assessments) && {
              id: 'assessments',
              label: S.tabs.assessments,
              icon: ClipboardCheck,
            },
            featureOn(FEATURES.Surveys) && {
              id: 'surveys',
              label: S.tabs.surveys,
              icon: ListChecks,
            },
          ].filter(Boolean),
        },
        {
          items: [
            { id: 'policies', label: S.tabs.policies, icon: ShieldCheck },
            featureOn(FEATURES.Phishing) && {
              id: 'phishing',
              label: S.tabs.phishing,
              icon: Fish,
            },
            featureOn(FEATURES.SecurityReports) && {
              id: 'securityReports',
              label: S.tabs.securityReports,
              icon: Siren,
            },
          ].filter(Boolean),
        },
        {
          items: [
            featureOn(FEATURES.Gamififcation) && {
              id: 'points',
              label: S.tabs.points,
              icon: Trophy,
            },
          ].filter(Boolean),
        },
      ].filter((group) => group.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.features],
  )

  /* The sections this licence actually grants, so a hash naming one it does not falls
     back instead of selecting a tab that renders nothing. */
  const tabIds = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => item.id)),
    [groups],
  )

  /**
   * The tab named in the URL hash — read AND written.
   *
   * `legacyRoutes.js` forwards `/userspoliciesdetails?username=` to `#policies` and
   * `/resetexamcounter?username=` to `#examTries` — those hashes did nothing until this
   * read them, so both landed on the activities tab and left the user to find the one
   * their link was about.
   *
   * It was only ever read, though, and once: choosing a section never wrote it back, so
   * the hash went on naming whatever the page arrived with and every re-entry — Back out
   * of a programme, a refresh, the same user opened again — collapsed eleven sections to
   * the first. `useHashTab` keeps the two in step, as the other tabbed screens do.
   */
  const [tab, setTab] = useHashTab('activities', tabIds)

  if (loading) {
    return (
      <>
        <PageHeader title={S.title} onBack={goBack} />
        <PageLoader />
      </>
    )
  }

  if (!user) {
    return (
      <>
        <PageHeader title={S.title} onBack={goBack} />
        <Card>
          <ErrorState title={S.errorHeading} description={S.errorBody} />
        </Card>
      </>
    )
  }

  return (
    <>
      {/* The action moves up here from the bottom of the profile card, where it
          sat under eight read-only rows with nothing around it. */}
      <PageHeader
        onBack={goBack}
        title={S.title}
        subtitle={user.name}
        actions={
          <Button onClick={() => toast.success('تم جدولة البريد بنجاح')}>
            <Mail size={16} strokeWidth={1.75} />
            {S.profile.sendEmail}
          </Button>
        }
      />

      {/*
       * Who this is, and where they stand — one band.
       *
       * It used to carry the USERNAME as its heading, with the person's actual
       * name buried as row one of the panel below. And a page called التقارير
       * الشخصية opened with no figure on it anywhere: points, risk and badges
       * were three tabs away. Both are on the band now, and neither costs a
       * request — `getUser` already returns all of it.
       */}
      <IdentityBand
        name={user.name}
        meta={[
          { icon: AtSign, text: user.userName, ltr: true },
          ...(user.department ? [{ icon: LayoutGrid, text: user.department }] : []),
          { icon: CalendarDays, text: asCell(user.startedOn).slice(0, 10), ltr: true },
        ]}
        status={user.status.map((badge) => (
          <StatusBadge key={badge.label} tone={badge.color}>
            {badge.label}
          </StatusBadge>
        ))}
      >
        <dl className="identity-figures">
          <Figure
            label={S.points.positive}
            value={user.pointsPositive ?? 0}
            tone="good"
            icon={TrendingUp}
          />
          {/* A zero here is good news, so it stays in ink — the red is reserved
              for a user who has actually lost points. */}
          <Figure
            label={S.points.negative}
            value={user.pointsNegative ?? 0}
            tone={user.pointsNegative > 0 ? 'bad' : undefined}
            icon={TrendingDown}
          />
          <Figure
            label={S.points.riskLevel}
            value={user.pointsRiskLevelName ?? S.standing.noRisk}
            icon={ShieldAlert}
          />
          <Figure
            label={S.standing.badges}
            value={`${earnedBadges} / ${totalBadges}`}
            icon={Award}
          />
        </dl>
      </IdentityBand>

      {/* The scope switch spans the page rather than sitting inside the content
          column: it selects what the whole page is showing, and across the full
          width the eleven sections fit on one line at 1440 and wrap cleanly
          below it. */}
      <div className="ud__tabs">
        <SectionNav groups={groups} value={tab} onChange={setTab} ariaLabel={S.title} />
      </div>

      {/* `البيانات` stops being a tab: the profile is the one thing every other tab
          is read against, so it is always on screen. 8 tabs become 6. */}
      <div className="ud__layout">
        <aside className="ud__profile">
          <Card title={S.tabs.attributes}>
            <dl className="ud__grid">
              <Field label={S.profile.arabicName} value={user.name} />
              <Field label={S.profile.englishName} value={user.nameEn} ltr />
              <Field label={S.profile.email} value={user.email} ltr />
              <Field label={S.profile.loginProvider} value={user.loginProvider || "-"} ltr />
              <Field
                label={S.profile.profileUpdateStatus}
                value={user.profileUpdated ? S.profile.updated : S.profile.notUpdated}
              />
              <Field label={S.profile.startDate} value={asCell(user.startedOn)} ltr />
              <Field label={S.profile.lastLogin} value={asCell(user.lastLogin)} ltr />
              <Field label={S.profile.license} value="-" />
            </dl>
          </Card>
        </aside>

        <div className="ud__main">
          {tab === 'activities' && <ActivitiesTab userId={user.userId} load={listActivities} />}
          {tab === 'statusLogs' && <StatusLogTab userId={user.userId} load={listStatusLog} />}
          {tab === 'policies' && <PoliciesTab userId={user.userId} load={listPolicies} />}
          {tab === 'programs' && <ProgramsTab userId={user.userId} />}
          {tab === 'examTries' && (
            <ExamTriesTab
              userId={user.userId}
              load={listExamTries}
              onReset={onResetTries}
              reloadKey={reloadKey}
            />
          )}
          {tab === 'phishing' && <PhishingTab userId={user.userId} load={listPhishing} />}
          {tab === 'securityReports' && (
            <SecurityReportsTab userId={user.userId} load={listSecurityReports} />
          )}
          {tab === 'assessments' && <AssessmentsTab userId={user.userId} load={listAssessments} />}
          {tab === 'surveys' && (
            <SurveysTab userId={user.userId} load={listSurveys} onDownload={onDownloadSurvey} />
          )}
          {/* One subject, two calls that both already exist: the totals on top,
              their log underneath. */}
          {tab === 'points' && (
            <div className="ud__points">
              <PointsTab user={user} />
              <PointsLogTab userId={user.userId} load={listPointsLog} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * `ltr` belongs on the TEXT RUN, not on the block.
 *
 * On the <dd> it also flipped the block's alignment, so an email or a timestamp
 * sat hard against the left edge of the panel while the Arabic label above it
 * stayed at the right — the pair read as two unrelated things. On the <bdi> it
 * only fixes the ordering of the run, which is all a Latin value needs, and the
 * value lines up under its label.
 */
function Field({ label, value, ltr = false }) {
  return (
    <div className="ud__field">
      <dt>
        <bdi>{label}</bdi>
      </dt>
      <dd>
        <bdi {...(ltr ? { dir: 'ltr' } : null)}>{value || '-'}</bdi>
      </dd>
    </div>
  )
}

/** One figure on the identity band: a glyph, the figure, what it counts. */
function Figure({ label, value, icon: Icon, tone }) {
  return (
    <div className={`identity-figure${tone ? ` is-${tone}` : ''}`}>
      <dt>
        <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
        <bdi>{label}</bdi>
      </dt>
      <dd>
        <bdi>{typeof value === 'number' ? value.toLocaleString('en-US') : value}</bdi>
      </dd>
    </div>
  )
}
